import React from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag, ChevronLeft, Bell, ShieldCheck, Truck, Zap } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext'; 
import { motion } from 'framer-motion';
import axios from 'axios';
import SEO from '../components/SEO';

const Cart = () => {
  const { cartItems, updateQuantity, removeFromCart, subtotal } = useCart();
  const { isDarkMode } = useTheme(); 
  const shipping = subtotal > 2000 || cartItems.length === 0 ? 0 : 150;
  const total = subtotal + shipping;

  // --- NOTIFY ME LOGIC ---
  const handleNotifyMe = async (productId, name) => {
    const email = prompt(`Enter email to get notified for ${name}:`);
    if (email) {
      try {
        await axios.post("https://wethrift-backend.onrender.com/api/product/notify-me", { productId, email });
        alert("Waitlist updated! We'll mail you.");
      } catch (err) {
        alert("Failed to register.");
      }
    }
  };

  // --- EMPTY CART STATE ---
  if (cartItems.length === 0) {

    return (      


      <div className={`min-h-screen flex flex-col items-center justify-center space-y-12 px-6 transition-colors duration-500 ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-white text-black'}`}>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative">
          <div className={`absolute inset-0 blur-[120px] rounded-full ${isDarkMode ? 'bg-emerald-500/20' : 'bg-black/5'}`}></div>
          <ShoppingBag size={120} className={`relative z-10 ${isDarkMode ? 'text-emerald-500' : 'text-zinc-200'}`} />
        </motion.div>
        
        <div className="text-center space-y-4">
          <h2 className="text-6xl md:text-8xl font-black uppercase italic tracking-tighter leading-none">Archive_Empty</h2>
          <p className="text-zinc-500 text-[11px] font-black uppercase tracking-[0.5em]">No Assets Detected in Current Session</p>
        </div>

        <Link to="/products">
          <button className={`px-16 py-6 rounded-full font-black uppercase tracking-[0.3em] text-[10px] transition-all border ${isDarkMode ? 'bg-emerald-500 text-black border-emerald-500' : 'bg-black text-white border-black'}`}>
            Initialize_Collection
          </button>
        </Link>
      </div>
    );
  }

  const hasOutOfStockItems = cartItems.some(item => {
    const stockForSize = item.sizeStock?.find(s => s.size === item.selectedSize)?.quantity || 0;
    return stockForSize <= 0;
  });

  return (

        <>
    
    <SEO 
  title="Your Bag | Ready for Dispatch" 
  description="Review your selected assets. Complete your acquisition and secure your rare finds before they're gone."
  url="/cart"
/>
    <div className={`min-h-screen pt-32 pb-20 transition-colors duration-500 ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-white text-black'}`}>
      
      {/* HEADER SECTION */}
      <div className="max-w-7xl mx-auto px-6 mb-16">
        <Link to="/products" className={`inline-flex items-center gap-3 transition mb-12 text-[10px] uppercase font-black tracking-[0.3em] ${isDarkMode ? 'text-emerald-500/60 hover:text-emerald-500' : 'text-zinc-400 hover:text-black'}`}>
          <ChevronLeft size={16} /> Continue_Acquisition
        </Link>
        
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
               <div className="w-12 h-[2px] bg-emerald-500"></div>
               <span className="text-emerald-500 font-black text-[10px] tracking-[0.4em] uppercase">Security_Encrypted_Cart</span>
            </div>
            <h1 className="text-7xl md:text-9xl font-black uppercase italic tracking-tighter leading-[0.8]">
              Your <span className={isDarkMode ? 'text-zinc-800' : 'text-zinc-200'}>Bag</span>
            </h1>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 px-6 py-4 rounded-3xl backdrop-blur-xl">
             <p className="text-zinc-500 font-black uppercase text-[10px] tracking-[0.3em]">
               System_Load: <span className="text-emerald-500">{cartItems.length} UNITS</span>
             </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">

          {/* 📦 LEFT: ITEMS LIST */}
          <div className="lg:flex-[1.8] space-y-8">
            {cartItems.map(item => {
              const isOutOfStock = (item.sizeStock?.find(s => s.size === item.selectedSize)?.quantity || 0) <= 0;
              return (
                <div 
                  key={`${item.id}-${item.selectedSize}`} 
                  className={`relative flex flex-col sm:flex-row gap-8 rounded-[3rem] p-8 border transition-all duration-700 ${isDarkMode ? 'bg-zinc-950/50 border-white/5 shadow-2xl' : 'bg-zinc-50 border-black/5'}`}
                >
                  {/* Image Chassis */}
                  <div className={`w-full sm:w-48 aspect-[3/4] rounded-[2rem] overflow-hidden flex-shrink-0 border relative ${isDarkMode ? 'bg-black border-white/10 shadow-inner' : 'bg-zinc-200 border-black/10'}`}>
                   <img 
    src={item.img || item.image} 
    // 🔥 SEO: Dynamic Alt Text (Brand + Name)
    alt={`${item.brand || 'Vintage'} ${item.name} | THE_ARCHIVE Asset`} 
    
    className={`w-full h-full object-cover ${isOutOfStock ? 'grayscale opacity-20' : 'grayscale group-hover:grayscale-0'} transition-all duration-1000`} 
    
    // ⚡ Performance: Badi companies wala loading logic
    loading="lazy" 
    
    // 🛡️ Security: Backend/Cloudinary images ke liye
    crossOrigin="anonymous" 
  />
                    {isOutOfStock && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="bg-red-600 text-white text-[8px] font-black uppercase tracking-widest px-3 py-1 rotate-[-15deg] shadow-2xl">Depleted</span>
                      </div>
                    )}
                  </div>

                  {/* Info Chassis */}
                  <div className="flex-1 flex flex-col justify-between py-2">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <span className="text-emerald-500 text-[9px] font-black uppercase tracking-[0.4em]">{item.brand}</span>
                        <h3 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter leading-tight text-white">{item.name}</h3>
                        <div className="flex gap-2 pt-2">
                          <span className="text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">Config: {item.selectedSize}</span>
                        </div>
                      </div>
                      <button onClick={() => removeFromCart(item.id, item.selectedSize)} className="p-4 bg-red-500/5 text-red-500 rounded-2xl border border-red-500/10">
                        <Trash2 size={18} />
                      </button>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mt-12">
                      {/* Quantity Logic */}
                      <div className={`flex items-center w-fit rounded-2xl border ${isDarkMode ? 'bg-black border-white/10' : 'bg-white border-black/10'}`}>
                        <button disabled={isOutOfStock} onClick={() => updateQuantity(item.id, item.selectedSize, -1)} className="p-4 text-zinc-500 border-r border-white/5">
                          <Minus size={14} />
                        </button>
                        <span className="w-12 text-center font-black italic text-sm text-emerald-500">{item.quantity}</span>
                        <button disabled={isOutOfStock} onClick={() => updateQuantity(item.id, item.selectedSize, 1)} className="p-4 text-zinc-500 border-l border-white/5">
                          <Plus size={14} />
                        </button>
                      </div>

                      {isOutOfStock ? (
                        <button onClick={() => handleNotifyMe(item.id, item.name)} className="flex items-center gap-2 bg-white text-black px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest">
                          <Bell size={14}/> Notify_Restock
                        </button>
                      ) : (
                        <div className="text-right">
                          <p className="text-[9px] font-black text-zinc-600 uppercase tracking-widest mb-1">Unit_Value</p>
                          <span className="text-4xl font-black italic tracking-tighter text-white">₹{item.price * item.quantity}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 💰 RIGHT: SUMMARY (The Intelligence Panel) */}
          <div className="lg:flex-[1]">
            <div className={`border rounded-[3.5rem] p-10 sticky top-32 space-y-12 overflow-hidden ${isDarkMode ? 'bg-zinc-950 border-white/5 shadow-[0_0_50px_rgba(0,0,0,0.5)]' : 'bg-zinc-50 border-black/5'}`}>
              
              {/* Decorative Glow */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-[80px]"></div>

              <div className="space-y-2">
                <h2 className="text-4xl font-black uppercase italic tracking-tighter">Order_Intel</h2>
                <p className="text-[10px] font-black text-zinc-600 tracking-[0.3em] uppercase">Transaction_ID: #{Math.floor(Math.random()*900000)}</p>
              </div>

              <div className="space-y-6">
                <div className="flex justify-between text-[11px] font-black uppercase tracking-widest text-zinc-500">
                  <span>Gross_Subtotal</span>
                  <span className="text-white">₹{subtotal}</span>
                </div>
                <div className="flex justify-between text-[11px] font-black uppercase tracking-widest text-zinc-500">
                  <span>Logistic_Fee</span>
                  <span className={shipping === 0 ? "text-emerald-500" : "text-white"}>
                    {shipping === 0 ? "COMPLEMENTARY" : `₹${shipping}`}
                  </span>
                </div>
                
                <div className={`pt-8 border-t flex flex-col gap-2 ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}>
                   <span className="text-[10px] font-black uppercase tracking-[0.4em] text-zinc-600">Total_Asset_Commitment</span>
                   <div className="flex justify-between items-end">
                      <span className="text-2xl font-black uppercase italic tracking-tight text-white underline decoration-emerald-500 decoration-4 underline-offset-8">Total</span>
                      <span className="text-6xl font-black italic tracking-tighter text-emerald-500">₹{total}</span>
                   </div>
                </div>
              </div>

              {/* Progress to Free Shipping */}
              <div className="space-y-4">
                 <div className="h-1.5 w-full bg-zinc-900 rounded-full overflow-hidden border border-white/5">
                    <div 
                       className="h-full bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)] transition-all duration-1000"
                       style={{ width: `${Math.min((subtotal / 2000) * 100, 100)}%` }}
                    ></div>
                 </div>
                 <p className="text-[9px] font-black uppercase tracking-widest text-zinc-500 text-center">
                   {subtotal >= 2000 ? "Level: Free Shipping Unlocked" : `Add ₹${2000 - subtotal} to unlock Logistic_Subsidy`}
                 </p>
              </div>

              {hasOutOfStockItems ? (
                <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-[2rem] text-center">
                  <p className="text-[10px] font-black uppercase text-red-500 leading-relaxed">
                    Conflict Detected: Depleted Stock in Assets. Remove to proceed.
                  </p>
                </div>
              ) : (
                <Link to="/checkout" className="block">
                  <button className={`w-full py-7 rounded-[2.5rem] font-black uppercase tracking-[0.3em] text-[11px] flex items-center justify-center gap-4 transition-all shadow-2xl ${isDarkMode ? 'bg-emerald-500 text-black' : 'bg-black text-white'}`}>
                    Initialize_Checkout <ArrowRight size={20} />
                  </button>
                </Link>
              )}

              {/* Trust Hub */}
              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/5 opacity-40">
                 <div className="flex flex-col items-center gap-2">
                    <ShieldCheck size={18} className="text-emerald-500"/>
                    <span className="text-[7px] font-black tracking-widest uppercase">Safe_Vault</span>
                 </div>
                 <div className="flex flex-col items-center gap-2">
                    <Truck size={18} className="text-emerald-500"/>
                    <span className="text-[7px] font-black tracking-widest uppercase">Global_Exp</span>
                 </div>
                 <div className="flex flex-col items-center gap-2">
                    <Zap size={18} className="text-emerald-500"/>
                    <span className="text-[7px] font-black tracking-widest uppercase">Fast_Sync</span>
                 </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Cart;